/* One absolute clock for every placement. No per-visitor storage or network polling. */
(() => {
  const campaign = window.DripnordEarlyAccess;
  if (!campaign || campaign.initialized) return;
  campaign.initialized = true;
  const root = document.documentElement;
  const instances = new Set();
  const pendingScopes = new Set();
  let timer = null;
  let queued = false;
  let lastState = root.getAttribute('data-dn-ea-state');
  const setText = (node, value) => { if (node && node.textContent !== value) node.textContent = value; };
  const setHidden = (node, hidden) => { if (node && node.hidden !== hidden) node.hidden = hidden; };
  const within = (scope, selector) => [...(scope.matches?.(selector) ? [scope] : []), ...scope.querySelectorAll(selector)];

  function normalizePresentation(scope) {
    if (campaign.isActive() || !scope?.querySelectorAll) return;
    // Only campaign markup is normalized. Current prices, genuine discounts,
    // availability and checkout data are never rewritten by expiry.
    within(scope, '[data-dn-ea-price-label]').forEach(label => setText(label, label.dataset.currentLabel || ''));
    within(scope, '.price--early-access').forEach(price => price.classList.remove('price--early-access', 'price--on-sale'));
    within(scope, '.product-card__info--early-access').forEach(info => info.classList.remove('product-card__info--early-access'));
    within(scope, '.product__price--early-access').forEach(price => price.classList.remove('product__price--early-access'));
    within(scope, '[data-product-badges]').forEach(badges => {
      const promo = badges.querySelector('.product-card__badge--early-access');
      if (promo) { promo.hidden = true; setText(promo, ''); }
      badges.classList.remove('product-card__badges--early-access', 'product-card__badges--early-access-active');
      if (!badges.querySelector('.product-card__badge--sold-out, [data-product-condition-badge]')) badges.hidden = true;
    });
  }

  function queuePresentation(scope) {
    if (campaign.isActive()) return;
    pendingScopes.add(scope);
    if (queued) return;
    queued = true;
    queueMicrotask(() => {
      queued = false;
      pendingScopes.forEach(node => { if (node?.isConnected) normalizePresentation(node); });
      pendingScopes.clear();
    });
  }

  function protectFocusedCampaignControl() {
    const focused = document.activeElement;
    const item = focused?.closest('[data-dn-ea-active]');
    if (!item) return;
    if (item.hasAttribute('data-dn-ea-menu') && !focused.closest('dialog')) {
      const sibling = Array.from(item.parentElement?.querySelectorAll('a[href], button') || []).find(node => !node.closest('[data-dn-ea-active], [hidden], [inert]') && node.getClientRects().length);
      sibling?.focus({ preventScroll: true });
      return;
    }
    const drawer = focused.closest('dialog');
    if (drawer?.open) {
      const group = item.parentElement;
      const sibling = Array.from(group?.querySelectorAll('a[href], button') || []).find(node => !node.closest('[data-dn-ea-active], [hidden], [inert]') && node.getClientRects().length);
      const close = drawer.querySelector('[data-nav-close]');
      (sibling || close)?.focus({ preventScroll: true });
    } else {
      // A focused announcement is replaced by original, focusable content;
      // normal pointer browsing and unrelated keyboard focus are untouched.
      const announcement = focused.closest('.js-announcement-bar');
      const section = focused.closest('section, .shopify-section');
      const next = announcement?.querySelector('[data-dn-ea-announcement-original] a[href], [data-dn-ea-announcement-original] button') ||
        section?.querySelector('[data-dn-ea-inactive] a[href], [data-dn-ea-expired] a[href], [data-dn-ea-focus-fallback]') ||
        (announcement && Array.from(document.querySelectorAll('#header a[href], #header button')).find(node => !node.closest('[data-dn-ea-active], [hidden], [inert]') && node.getClientRects().length));
      return next;
    }
  }

  function tick() {
    clearTimeout(timer);
    timer = null;
    const state = campaign.state();
    let restoreAnnouncementFocus;
    if (state !== lastState && state !== 'active') restoreAnnouncementFocus = protectFocusedCampaignControl();
    if (root.getAttribute('data-dn-ea-state') !== state) root.setAttribute('data-dn-ea-state', state);
    if (state !== lastState) {
      lastState = state;
      if (state !== 'active') normalizePresentation(document);
      document.dispatchEvent(new CustomEvent('dripnord:early-access-change', { detail: { state } }));
      if (restoreAnnouncementFocus?.getClientRects().length) restoreAnnouncementFocus.focus({ preventScroll: true });
      window.dispatchEvent(new Event('resize'));
    }
    const remaining = campaign.deadline - Date.now();
    instances.forEach(instance => instance.update(remaining, state));
    if (state === 'active') {
      // The next displayed minute boundary, or exact expiry in the final minute.
      // Resume events recompute from Date.now after a throttled/suspended tab.
      const delay = remaining < 60000 ? remaining : (remaining % 60000) + 1;
      timer = setTimeout(tick, Math.max(1, Math.min(delay, 60000)));
    }
  }

  function formatWrittenDate(node, locale) {
    if (!node || !Number.isFinite(campaign.deadline)) return;
    try {
      const parts = new Intl.DateTimeFormat(locale.startsWith('fi') ? 'fi-FI' : 'en-GB', {
        timeZone: 'Europe/Helsinki', year: 'numeric', month: 'long', day: 'numeric',
        hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
      }).formatToParts(new Date(campaign.deadline));
      const values = Object.fromEntries(parts.map(part => [part.type, part.value]));
      values.month_number = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Helsinki', month: 'numeric' }).format(new Date(campaign.deadline));
      values.hour = values.hour.padStart(2, '0');
      const text = node.dataset.datePattern.replace(/__(day|month|month_number|year|hour|minute)__/g, (_, key) => values[key] || '');
      setText(node, text);
    } catch (_) { /* Keep the validated, localized Liquid fallback. */ }
  }

  class EarlyAccessCountdown extends HTMLElement {
    connectedCallback() {
      if (instances.has(this)) return;
      this.remaining = this.querySelector('[data-dn-ea-remaining]');
      this.digits = this.querySelector('[data-dn-ea-digits]');
      this.finalMinute = this.querySelector('[data-dn-ea-final-minute]');
      this.numbers = Object.fromEntries(Array.from(this.querySelectorAll('[data-dn-ea-number]')).map(node => [node.dataset.dnEaNumber, node]));
      formatWrittenDate(this.querySelector('[data-dn-ea-date]'), this.dataset.locale || 'en');
      instances.add(this);
      tick();
    }
    disconnectedCallback() { instances.delete(this); }
    update(remaining, state) {
      if (state !== 'active' || this.dataset.available === 'false') return;
      const underMinute = remaining < 60000;
      if (!underMinute) {
        const totalMinutes = Math.floor(remaining / 60000);
        const values = { days: Math.floor(totalMinutes / 1440), hours: Math.floor(totalMinutes / 60) % 24, minutes: totalMinutes % 60 };
        Object.keys(values).forEach(key => setText(this.numbers[key], String(values[key]).padStart(2, '0')));
      }
      setHidden(this.digits, underMinute);
      setHidden(this.finalMinute, !underMinute);
      setHidden(this.remaining, false);
      if (!this.hasAttribute('data-ready')) this.setAttribute('data-ready', '');
    }
  }
  class EarlyAccessPresentation extends HTMLElement {
    connectedCallback() {
      queuePresentation(this.closest('product-card, product-info, .cart-item') || this.closest('.price, [data-product-badges]') || this.parentElement);
    }
  }
  if (!customElements.get('dn-early-access-countdown')) customElements.define('dn-early-access-countdown', EarlyAccessCountdown);
  if (!customElements.get('dn-early-access-presentation')) customElements.define('dn-early-access-presentation', EarlyAccessPresentation);

  const announcementObserver = 'ResizeObserver' in window ? new ResizeObserver(() => window.dispatchEvent(new Event('resize'))) : null;
  function observeAnnouncements() {
    announcementObserver?.disconnect();
    document.querySelectorAll('.section-announcement-bar').forEach(bar => announcementObserver?.observe(bar));
  }
  function resume() { tick(); }
  document.addEventListener('visibilitychange', resume);
  window.addEventListener('pageshow', resume);
  document.addEventListener('shopify:section:load', event => {
    queuePresentation(event.target);
    observeAnnouncements();
    tick();
  });
  document.addEventListener('shopify:section:unload', () => { observeAnnouncements(); });
  document.addEventListener('product-info:loaded', event => queuePresentation(event.target));
  campaign.refresh = tick;
  campaign.normalizePresentation = normalizePresentation;
  normalizePresentation(document);
  observeAnnouncements();
  tick();
})();
